'use client';

import { BenefitsSection } from '@/components/sections/benefits-section';
import { CustomerVideos } from '@/components/sections/customer-videos';
import { FaqSection } from '@/components/sections/faq-section';
import { BubbleCall, SubpageHero } from '@/components/sections/subpage-hero';
// import { LiveCallDemo } from '@/components/voice/live-call-demo';
import Testimonials from '@/components/sections/testimonials';
import { TelephonyWall } from '@/components/voice/telephony-wall';
import { VoiceUseCaseGrid } from '@/components/voice/voice-use-case-grid';
import { useTranslation } from '@/lib/i18n/context';

const CONTENT = {
  en: {
    heroEyebrow: 'Channels',
    heroTitle: 'Phone support that picks up',
    heroSubtitleA: 'Every call answered on the first ring.',
    heroSubtitleB: 'By a voice that sounds like a real teammate.',
    bubbleCaller: '+1 (415) 555-0148',
    bubbleTopic: 'Refund inquiry · in progress',
  },
  ar: {
    heroEyebrow: 'القنوات',
    heroTitle: 'دعم هاتفي يرد فعلاً',
    heroSubtitleA: 'يرد على كل مكالمة من أول رنة.',
    heroSubtitleB: 'بصوت تشعر أنه زميل حقيقي في فريقك.',
    // ⁦ / ⁩ = LRI / PDI isolate. Without it, RTL bidi reorders the
    // number on screen to "555-0148 (415) 1+".
    bubbleCaller: '⁦+1 (415) 555-0148⁩',
    bubbleTopic: 'استفسار استرجاع · قيد المعالجة',
  },
} as const;

export default function VoiceAIPage() {
  const { t, locale } = useTranslation();
  const c = locale === 'ar' ? CONTENT.ar : CONTENT.en;

  return (
    <>
      <SubpageHero
        eyebrow={c.heroEyebrow}
        title={c.heroTitle}
        subtitle={
          <>
            {c.heroSubtitleA}
            <br />
            {c.heroSubtitleB}
          </>
        }
        videoSrc="/home/features/lady-on-the-phone.mp4"
        bubblePosition="top-right"
        worksWith={[
          { src: '/telephony/twilio.svg', alt: 'Twilio' },
          { src: '/telephony/ringcentral.svg', alt: 'RingCentral' },
          { src: '/telephony/zoom.svg', alt: 'Zoom Phone' },
          { src: '/telephony/vonage.svg', alt: 'Vonage' },
          { src: '/telephony/aircall.svg', alt: 'Aircall' },
          { src: '/telephony/dialpad.svg', alt: 'Dialpad' },
          { src: '/telephony/five9.svg', alt: 'Five9' },
        ]}
        bubbles={[
          {
            role: 'agent',
            widget: (
              <BubbleCall
                status="live"
                caller={c.bubbleCaller}
                topic={c.bubbleTopic}
              />
            ),
          },
        ]}
      />

      {/* Hidden for now — the "try our AI agent" interactive call demo.
          Restore by uncommenting both the import above and the line below. */}
      {/* <LiveCallDemo /> */}

      <CustomerVideos />

      <BenefitsSection
        title={t.channels['Voice AI Features']}
        subtitle={t.channels['Natural conversations powered by advanced AI']}
        learnMoreLink="#faq"
        accentColor="emerald"
        theme="light"
        benefits={[
          {
            title: t.channels['International Numbers'],
            description:
              t.channels[
                'Get local phone numbers in 100+ countries. No international fees for your customers.'
              ],
            linkText: '',
            linkHref: '#',
            videoUrl: '/product/voice-ai/numbers.mp4',
            imagePosition: 'right',
          },
          {
            title: t.channels['Custom Voices and Voice Cloning'],
            description:
              t.channels[
                'Clone your brand voice or create custom voices using ElevenLabs. Your AI sounds exactly how you want.'
              ],
            linkText: '',
            linkHref: '#',
            videoUrl: '/product/voice-ai/cloning.mp4',
            imagePosition: 'left',
          },
          {
            title: t.channels['High Concurrency Levels'],
            description:
              t.channels[
                'Handle up to 500 calls at once. No busy signals, no wait times, instant scaling.'
              ],
            linkText: '',
            linkHref: '#',
            videoUrl: '/product/voice-ai/concurrency.mp4',
            imagePosition: 'right',
          },
          {
            title: t.channels['Multi-lingual Support'],
            description:
              t.channels[
                'Speak to customers in their language. Supports 6+ languages with native pronunciation.'
              ],
            linkText: '',
            linkHref: '#',
            videoUrl: '/product/voice-ai/languages.mp4',
            imagePosition: 'left',
          },
          {
            title: t.channels['SIP Trunking and API First'],
            description:
              t.channels[
                'Connect via SIP trunking or API. Works with any phone system or build custom workflows.'
              ],
            linkText: '',
            linkHref: '#',
            videoUrl: '/product/voice-ai/api.mp4',
            imagePosition: 'right',
          },
        ]}
      />

      <TelephonyWall />

      <VoiceUseCaseGrid />

      {/* FAQ Section */}
      <FaqSection
        title={t.channels['Frequently asked questions']}
        subtitle={
          t.channels['Find answers to common questions about Voice AI.']
        }
        items={[
          {
            question: t.channels['How natural does the voice sound?'],
            answer:
              t.channels[
                "Agent 5 Voice uses advanced text-to-speech with natural intonation, pauses, and speech patterns. Most customers can't tell they're talking to AI."
              ],
          },
          {
            question: t.channels['Can it handle multiple languages?'],
            answer:
              t.channels[
                'Yes. Agent 5 Voice supports multiple languages with native pronunciation and cultural context understanding.'
              ],
          },
          {
            question: t.channels["What happens if the AI can't answer?"],
            answer:
              t.channels[
                'Agent 5 seamlessly transfers the call to a human agent with full conversation context, so your agent knows exactly what was discussed.'
              ],
          },
          {
            question: t.channels['Can it integrate with my phone system?'],
            answer:
              t.channels[
                'Yes. Agent 5 Voice works with major phone systems and can be configured to handle inbound calls through your existing setup.'
              ],
          },
          {
            question:
              t.channels['How does it handle accents and background noise?'],
            answer:
              t.channels[
                'Agent 5 Voice is trained on diverse speech patterns and accents, with advanced noise cancellation to maintain clarity even in challenging audio conditions.'
              ],
          },
        ]}
      />

      <Testimonials />
    </>
  );
}
